import constants from "./constants";
import { getLoadedTenantDocumentListsOrNull } from "../context/tenantDocumentLists/tenantDocumentListsStore";
import { getPayslipPeriodForDoc } from "./payslipRequirement";

// Freelancers need the RETA receipts (Recibo de cuota de autónomos) of the
// last 3 months, one slot each, like the 3 payslips. Skor lite returns every
// receipt as "Self-employed Social Security Payment"; a receipt fills the
// slot of its period_month / period_year. Receipts of other months fill no
// slot and stay additional documents.
export const RETA_ORDER = [
  constants.firstMonthRetaReceipt,
  constants.secondMonthRetaReceipt,
  constants.thirdMonthRetaReceipt,
];

export const isRetaRequired = (user) =>
  user?.employment_status === "self_employed" &&
  user?.employment_category === "freelancer";

// Name aliases come from the backend (GET /config/tenant-document-lists).
// Also used outside the loading gate (RETA receipts card on the profile
// page), so it must not throw: before the lists load it checks the raw name.
// Same result today: the only alias of the RETA name is the name itself.
export const isRetaDocName = (name) => {
  const docsMap = getLoadedTenantDocumentListsOrNull()?.docsMap ?? {};
  return (docsMap[name] ?? name) === constants.self_employed_ss_payment;
};

// Last 3 months before the month of `referenceDate` (most recent first),
// same months as getRequiredPayslipPeriods.
export const getRequiredRetaPeriods = (referenceDate = new Date()) => {
  const date = new Date(referenceDate);
  const periods = [];
  for (let i = 1; i <= 3; i++) {
    const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
    periods.push({
      period_year: d.getFullYear(),
      period_month: d.getMonth() + 1,
    });
  }
  return periods;
};

// Only the tenant's own receipts fill a slot. A receipt Skor lite files
// under another person's profile (data.is_uploader === false) does not.
const getFileName = (url) =>
  String(url ?? "")
    .split("?")[0]
    .split("/")
    .pop();
const getAnalysisProfiles = (user) =>
  user?.tenant_extra_field_id?.document_analysis?.profileReports ??
  user?.tenant_extra_id?.document_analysis?.profileReports ??
  [];
const getProfileDocs = (profile) => {
  const buckets = profile?.report?.documentBuckets;
  return Array.isArray(buckets)
    ? buckets.flatMap((bucket) => bucket?.documents || [])
    : [
        ...(profile?.report?.matchedDocuments ?? []),
        ...(profile?.report?.otherDocuments ?? []),
      ];
};
export const isOtherPersonsProfile = (profile) =>
  profile?.data?.is_uploader === false;

// File names of the documents Skor lite filed under other people.
export const getOtherPersonsFileNames = (user) =>
  new Set(
    (Array.isArray(getAnalysisProfiles(user)) ? getAnalysisProfiles(user) : [])
      .filter(isOtherPersonsProfile)
      .flatMap(getProfileDocs)
      .map((doc) => getFileName(doc?.data?.url ?? doc?.url))
      .filter(Boolean)
  );

// RETA analysis documents of the tenant's own profiles (any bucket).
export const getOwnRetaAnalysisDocs = (profiles = []) =>
  (Array.isArray(profiles) ? profiles : [])
    .filter((profile) => !isOtherPersonsProfile(profile))
    .flatMap(getProfileDocs)
    .filter((doc) => !doc?.isDuplicate && isRetaDocName(doc?.documentName));

export const isOtherPersonsDoc = (doc, otherFileNames) =>
  doc?.analysisIsUploader === false ||
  (otherFileNames?.has(getFileName(doc?.file_name)) ?? false);

// Slot index (0-2) of a period, or -1.
export const getRetaSlotIndex = (period, requiredPeriods) =>
  (requiredPeriods ?? getRequiredRetaPeriods()).findIndex(
    (required) =>
      required.period_year === Number(period?.period_year) &&
      required.period_month === Number(period?.period_month)
  );

// Gives each RETA tenant doc that fills a slot the slot's type
// ("1st month RETA receipt", ...). One doc per slot, newest first. Other docs
// are returned unchanged. Only for freelancers.
export const applyRetaSlotTypes = (
  docs = [],
  { user = null, forensicList = [], forensicByFile = {} } = {}
) => {
  const list = Array.isArray(docs) ? docs : [];
  if (!isRetaRequired(user)) return list;
  const requiredPeriods = getRequiredRetaPeriods();
  const otherFileNames = getOtherPersonsFileNames(user);
  const usedSlots = new Set();
  const slotByDoc = new Map();
  [...list]
    .filter(
      (doc) =>
        isRetaDocName(doc?.type) && !isOtherPersonsDoc(doc, otherFileNames)
    )
    .sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
    .forEach((doc) => {
      const slotIndex = getRetaSlotIndex(
        getPayslipPeriodForDoc(doc, forensicList, forensicByFile),
        requiredPeriods
      );
      if (slotIndex >= 0 && !usedSlots.has(slotIndex)) {
        usedSlots.add(slotIndex);
        slotByDoc.set(doc, RETA_ORDER[slotIndex]);
      }
    });
  return list.map((doc) =>
    slotByDoc.has(doc) ? { ...doc, type: slotByDoc.get(doc) } : doc
  );
};
